import { useNavigate } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useDebounceCallback,
} from "@devhop/ui";
import {
  BarChart3Icon,
  CheckIcon,
  ChevronDownIcon,
  EyeIcon,
  HelpCircle,
  SearchIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { HistoryListEntryDTO } from "@/modules/quotation-invoice/domain/types";
import { formatInTimeZone } from "date-fns-tz";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Label } from "@devhop/ui";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useQuotationInvoiceDocumentTypesQuery,
  useQuotationInvoiceHistoryQuery,
  useQuotationInvoiceHistorySummaryQuery,
  type HistoryListQuery,
} from "../api/queries";
import { DocumentAuditTour, useDocumentAuditTour } from "../tour/DocumentAuditTour";

const ACTION_LABELS: Record<string, string> = {
  draft_created: "ສ້າງຮ່າງ",
  issued: "ອອກເອກະສານ",
  converted_to_invoice: "ຕໍ່ເປັນໃບບິນ",
  converted_to_receipt: "ຕໍ່ເປັນໃບຮັບເງິນ",
  paid: "ຊຳລະແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

const DATE_RANGES = [
  { value: "0", label: "ທັງໝົດ" },
  { value: "7", label: "7 ວັນຫຼ້າສຸດ" },
  { value: "30", label: "30 ວັນຫຼ້າສຸດ" },
  { value: "90", label: "90 ວັນຫຼ້າສຸດ" },
];

const BANGKOK = "Asia/Bangkok";
const DB_DATETIME = "yyyy-MM-dd HH:mm:ss.SSS";

function getDateRange(days: number): { dateFrom: string; dateTo: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  const dateFrom = formatInTimeZone(from, BANGKOK, DB_DATETIME);
  const dateTo = formatInTimeZone(to, BANGKOK, DB_DATETIME);
  return { dateFrom, dateTo };
}

export function QuotationInvoiceDocumentAuditPage() {
  const nav = useNavigate({ from: "/app/documents/audit" });
  const [dateRangeDays, setDateRangeDays] = useState(0);
  const [documentTypeTab, setDocumentTypeTab] = useState<string>("all");
  const [searchQ, setSearchQ] = useState("");

  const { data: documentTypesData } = useQuotationInvoiceDocumentTypesQuery({
    limit: 100,
  });
  const documentTypeOptions = useMemo(() => {
    const list = documentTypesData?.items ?? [];
    const fromDb = [...list]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((t) => ({ value: t.code, label: t.name }));
    return [{ value: "all", label: "ທັງໝົດ" }, ...fromDb];
  }, [documentTypesData?.items]);
  const [searchQForQuery, setSearchQForQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const applySearch = useDebounceCallback((val: string) => {
    setSearchQForQuery(val.trim());
    setOffset(0);
  }, 400);

  const { dateFrom, dateTo } = useMemo(
    () =>
      dateRangeDays <= 0
        ? { dateFrom: undefined, dateTo: undefined }
        : getDateRange(dateRangeDays),
    [dateRangeDays],
  );

  const historyQuery: HistoryListQuery = useMemo(
    () => ({
      offset,
      limit,
      dateFrom,
      dateTo,
      documentType:
        documentTypeTab && documentTypeTab !== "all" ? documentTypeTab : undefined,
      q: searchQForQuery || undefined,
    }),
    [offset, limit, dateFrom, dateTo, documentTypeTab, searchQForQuery],
  );

  const { data: historyData, isLoading } =
    useQuotationInvoiceHistoryQuery(historyQuery);
  const { data: summaryData } = useQuotationInvoiceHistorySummaryQuery();

  const timelineEntries = useMemo(() => {
    if (!historyData?.data) return [];
    return historyData.data.slice(0, 10);
  }, [historyData]);

  const total = historyData?.total ?? 0;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);

  const handleViewDocument = (id: string) => {
    nav({ to: "/app/documents/$id", params: { id } });
  };

  const tour = useDocumentAuditTour();

  return (
    <>
      <Header />
      <Main>
        <DocumentAuditTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="space-y-6">
          <div data-tourid="document-audit-header">
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold tracking-tight">
                ປະຫວັດການກວດສອບເອກະສານ
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              ຕິດຕາມຍ້ອນຫຼັງ ແລະ ຈັດການວົງຈອນຊີວິດເອກະສານການເງິນ
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
            {/* Left: Activity Timeline */}
            <Card className="h-fit" data-tourid="document-audit-timeline">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <BarChart3Icon className="h-4 w-4" />
                  ກິດຈະກຳຫຼ້າສຸດ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <div className="relative space-y-4">
                  <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
                  {timelineEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="relative flex gap-3 pl-8"
                    >
                      <span className="absolute left-0 flex h-6 w-6 items-center justify-center rounded-full border-2 border-primary bg-background">
                        {entry.action === "paid" ? (
                          <CheckIcon className="h-3 w-3 text-primary" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-muted-foreground" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1 pb-4">
                        <p className="font-medium text-foreground text-sm">
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {entry.documentNumber ?? entry.documentId.slice(0, 8)} •{" "}
                          {formatDateTimeLocal(entry.performedAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-2 px-0"
                  onClick={() => setOffset(0)}
                >
                  ເບິ່ງທັງໝົດ
                </Button>
              </CardContent>
            </Card>

            {/* Right: Audit Log Table */}
            <Card data-tourid="document-audit-table">
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="shrink-0 text-muted-foreground text-sm">
                    ປະເພດ:
                  </Label>
                  <SimpleSelect
                    value={documentTypeTab}
                    onValueChange={(val) => {
                      setDocumentTypeTab(val);
                      setOffset(0);
                    }}
                    placeholder="ທັງໝົດ"
                    className="h-9 min-w-[140px]"
                    options={documentTypeOptions}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="ຄົ້ນຫາເລກທີ, ລູກຄ້າ, ຫຼື ການກະທຳ..."
                      value={searchQ}
                      onChange={(e) => {
                        setSearchQ(e.target.value);
                        applySearch(e.target.value);
                      }}
                      className="h-9 w-64 rounded-md border border-input bg-background pl-8 pr-3 text-sm"
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={dateRangeDays}
                      onChange={(e) => {
                        setDateRangeDays(Number(e.target.value));
                        setOffset(0);
                      }}
                      className="h-9 appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                    >
                      {DATE_RANGES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                        <th className="px-4 py-3 font-medium">ປະເພດກິດຈະກຳ</th>
                        <th className="px-4 py-3 font-medium">ເລກທີເອກະສານ</th>
                        <th className="px-4 py-3 font-medium">ລູກຄ້າ</th>
                        <th className="px-4 py-3 font-medium">ວັນທີ & ເວລາ</th>
                        <th className="px-4 py-3 font-medium">ຜູ້ປະຕິບັດ</th>
                        <th className="w-12 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-8 text-center text-muted-foreground"
                          >
                            ກຳລັງໂຫຼດ...
                          </td>
                        </tr>
                      )}
                      {!isLoading &&
                        (historyData?.data ?? []).map((entry: HistoryListEntryDTO) => (
                          <tr
                            key={entry.id}
                            className="border-b transition-colors hover:bg-muted/50"
                          >
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className={`h-2 w-2 rounded-full ${
                                    entry.action === "paid"
                                      ? "bg-emerald-500"
                                      : entry.action === "issued"
                                        ? "bg-blue-500"
                                        : "bg-muted-foreground"
                                  }`}
                                />
                                {ACTION_LABELS[entry.action] ?? entry.action}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() =>
                                  handleViewDocument(entry.documentId)
                                }
                                className="font-medium text-primary hover:underline"
                              >
                                {entry.documentNumber ??
                                  entry.documentId.slice(0, 8)}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {entry.customerName}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {formatDateTimeLocal(entry.performedAt)}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {entry.performedByName ?? "—"}
                            </td>
                            <td className="px-4 py-3">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  handleViewDocument(entry.documentId)
                                }
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      {!isLoading &&
                        (!historyData?.data || historyData?.data.length === 0) && (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-4 py-8 text-center text-muted-foreground"
                            >
                              ບໍ່ພົບລາຍການ
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
                {total > 0 && (
                  <div className="flex items-center justify-between border-t px-4 py-3">
                    <p className="text-muted-foreground text-sm">
                      ສະແດງ {from}–{to} ຈາກ {total} ລາຍການ
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset === 0}
                        onClick={() => setOffset((o) => Math.max(0, o - limit))}
                      >
                        ກ່ອນໜ້າ
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset + limit >= total}
                        onClick={() =>
                          setOffset((o) => Math.min(total - limit, o + limit))
                        }
                      >
                        ຖັດໄປ
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-3" data-tourid="document-audit-summary">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ລາຍການ 24 ຊົ່ວໂມງຫຼ້າສຸດ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  {summaryData?.last24hCount ?? "—"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ເອກະສານທັງໝົດ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  {summaryData?.totalDocumentsCount ?? "—"}
                </p>
                <p className="text-muted-foreground text-xs">
                  ລະບົບບັນທຶກ
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ສະຖານະລະບົບ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-600">100%</p>
                <p className="flex items-center gap-1 text-emerald-600 text-xs">
                  <CheckIcon className="h-3 w-3" />
                  ປົກກະຕິ
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}
